import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown, Link2 } from 'lucide-react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  compareTasks,
  countLabel,
  doneLabel,
  dueLabel,
  formatWeekdayDate,
  isDoneRecently,
  isDueToday,
  isOverdue,
  leadLabel,
  leadProgress,
  matchesStatusFilter,
  matchesTaskQuery,
  memberName,
  taskStatus,
  teamStats,
  type TeamSortKey,
  type TeamStatusFilter,
} from '../taskViews';
import type { Task, TaskMember } from '../types';
import {
  MobileCreateButton,
  PriorityPill,
  SegmentedControl,
  StatusPill,
  TASK_COMPACT_TRIGGER_CLASS,
  TONE_TEXT_CLASS,
  TaskAvatar,
} from './taskUi';

const STATUS_OPTIONS: { value: TeamStatusFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'open', label: 'Offen' },
  { value: 'overdue', label: 'Überfällig' },
  { value: 'today', label: 'Heute' },
  { value: 'done', label: 'Erledigt' },
  { value: 'done7', label: 'Erledigt (7 T.)' },
];

const COLUMNS: { key: TeamSortKey; label: string }[] = [
  { key: 'title', label: 'To-do' },
  { key: 'lead', label: 'Anfrage' },
  { key: 'who', label: 'Zuständig' },
  { key: 'due', label: 'Fällig' },
  { key: 'prio', label: 'Priorität' },
  { key: 'status', label: 'Status' },
];

const TABLE_GRID = 'grid grid-cols-[minmax(220px,2fr)_minmax(170px,1.4fr)_150px_140px_90px_100px] gap-3 px-4';
const PANEL_CLASS = 'flex flex-col rounded-xl border border-app-border bg-app-surface shadow-sm';
const PANEL_HEAD_CLASS = 'flex items-center justify-between gap-3 border-b border-app-border px-4 py-3.5';

type Props = {
  tasks: Task[];
  loading: boolean;
  members: TaskMember[];
  membersById: Map<string, TaskMember>;
  today: string;
  selectedId: string | null;
  instancePrefix: string;
  canOpenProjects: boolean;
  query: string;
  onQuery: (q: string) => void;
  viewSelect?: React.ReactNode;
  /** Mobil: „+"-Knopf rechts oben */
  onCreate?: () => void;
  onSelect: (task: Task) => void;
};

export function TaskTeamOverview({
  tasks,
  loading,
  members,
  membersById,
  today,
  selectedId,
  instancePrefix,
  canOpenProjects,
  query,
  onQuery,
  viewSelect,
  onCreate,
  onSelect,
}: Props) {
  const [status, setStatus] = React.useState<TeamStatusFilter>('open');
  const [who, setWho] = React.useState<string>('all');
  const [sort, setSort] = React.useState<{ key: TeamSortKey; dir: 1 | -1 }>({ key: 'due', dir: 1 });

  const count = (fn: (t: Task) => boolean) => tasks.filter(fn).length;
  const nOverdue = count((t) => isOverdue(t, today));
  const kpis: { id: TeamStatusFilter; label: string; value: number; sub: string; valueClass: string }[] = [
    { id: 'open', label: 'Offen', value: count((t) => !t.done), sub: 'im gesamten Team', valueClass: 'text-app-text' },
    {
      id: 'overdue',
      label: 'Überfällig',
      value: nOverdue,
      sub: 'Fälligkeit überschritten',
      valueClass: nOverdue ? 'text-red-600' : 'text-app-text',
    },
    {
      id: 'today',
      label: 'Heute fällig',
      value: count((t) => isDueToday(t, today)),
      sub: formatWeekdayDate(today),
      valueClass: 'text-app-text',
    },
    {
      id: 'done7',
      label: 'Erledigt',
      value: count((t) => isDoneRecently(t, today)),
      sub: 'in den letzten 7 Tagen',
      valueClass: 'text-green-600',
    },
  ];

  // Nur Mitarbeiter mit Web-Zugang — reine App-Nutzer sehen die Aufgaben-Seite nicht.
  const stats = teamStats(tasks, members.filter((m) => m.web_access), today);
  const maxLoad = Math.max(1, ...stats.map((s) => s.open + s.doneRecently));
  const pct = (n: number) => `${(n / maxLoad) * 100}%`;
  const projects = leadProgress(tasks, today);

  const rows = tasks
    .filter((t) => matchesStatusFilter(t, status, today))
    .filter((t) => who === 'all' || t.assigned_user_id === who)
    .filter((t) => matchesTaskQuery(t, query, membersById))
    .sort(
      (a, b) =>
        sort.dir * compareTasks(sort.key, a, b, { today, members: membersById }) ||
        compareTasks('due', a, b, { today, members: membersById }),
    );

  const whoOptions = [
    { value: 'all', label: 'Alle Mitarbeiter' },
    ...members.map((m) => ({ value: m.id, label: memberName(m) })),
  ];
  const hasFilter = status !== 'all' || who !== 'all';

  return (
    <section className="app-scrollbar min-w-0 flex-1 overflow-y-auto bg-app-bg">
      <div className="flex max-w-[1440px] flex-col gap-4 p-4 md:p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-lg font-semibold leading-tight text-app-text">Team-Übersicht</h1>
            <span className="text-sm text-app-text-subtle">Stand aller To-dos · {formatWeekdayDate(today)}</span>
          </div>
          {onCreate && <MobileCreateButton onClick={onCreate} />}
          {viewSelect}
        </div>

        {loading ? (
          <InlineLoadingState label="To-dos werden geladen…" className="py-16" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[repeat(auto-fit,minmax(170px,1fr))] md:gap-3">
              {kpis.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  aria-pressed={status === k.id}
                  onClick={() => setStatus(k.id)}
                  className={`flex min-w-0 flex-col items-start gap-1 rounded-xl border bg-app-surface px-3.5 py-3 text-left md:px-4 md:py-3.5 shadow-sm transition-colors hover:bg-app-hover ${
                    status === k.id ? 'border-pipedrive-blue ring-1 ring-pipedrive-blue' : 'border-app-border'
                  }`}
                >
                  <span className="text-xs font-medium text-app-text-subtle">{k.label}</span>
                  <span className={`text-2xl font-semibold leading-tight tabular-nums md:text-[28px] ${k.valueClass}`}>{k.value}</span>
                  <span className="max-w-full truncate text-xs text-app-text-subtle">{k.sub}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(380px,100%),1fr))] gap-4">
              <section className={PANEL_CLASS}>
                <div className={PANEL_HEAD_CLASS}>
                  <span className="text-sm font-semibold text-app-text">Auslastung nach Mitarbeiter</span>
                  <div className="flex flex-wrap items-center gap-3 text-[11px] text-app-text-subtle">
                    <Legend className="bg-red-500" label="Überfällig" />
                    <Legend className="bg-app-border-strong" label="Offen" />
                    <Legend className="bg-green-500" label="Erledigt (7 T.)" />
                  </div>
                </div>
                <div className="flex flex-col px-2 pb-2.5 pt-1.5">
                  {stats.length === 0 && <EmptyLine text="Keine Mitarbeiter." />}
                  {stats.map((s) => {
                    const active = who === s.member.id;
                    return (
                      <button
                        key={s.member.id}
                        type="button"
                        title="To-dos filtern"
                        aria-pressed={active}
                        onClick={() => setWho(active ? 'all' : s.member.id)}
                        className={`grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 rounded-md px-2 py-2.5 text-left transition-colors md:grid-cols-[28px_minmax(90px,140px)_minmax(0,1fr)_auto] md:gap-y-0 ${
                          active ? 'bg-inbox-selected' : 'hover:bg-app-hover'
                        }`}
                      >
                        <span className="row-span-2 md:row-span-1">
                          <TaskAvatar member={s.member} size="lg" />
                        </span>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-medium text-app-text">{memberName(s.member)}</span>
                          <span className="text-xs text-app-text-subtle">
                            {s.open} offen · {s.doneRecently} erledigt
                          </span>
                        </span>
                        {/* Mobil rutscht der Balken in eine eigene Zeile unter Name und Status */}
                        <span className="col-span-2 col-start-2 row-start-2 flex h-2 overflow-hidden rounded bg-app-bg md:col-span-1 md:col-start-auto md:row-start-auto">
                          <span className="h-full bg-red-500" style={{ width: pct(s.overdue) }} />
                          <span className="h-full bg-app-border-strong" style={{ width: pct(s.open - s.overdue) }} />
                          <span className="h-full bg-green-500" style={{ width: pct(s.doneRecently) }} />
                        </span>
                        <span
                          className={`col-start-3 row-start-1 whitespace-nowrap text-right text-xs font-medium md:col-start-auto md:row-start-auto md:min-w-[72px] ${
                            s.overdue ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {s.overdue ? `${s.overdue} überfällig` : 'im Plan'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className={PANEL_CLASS}>
                <div className={PANEL_HEAD_CLASS}>
                  <span className="text-sm font-semibold text-app-text">Stand nach Anfrage</span>
                </div>
                <div className="flex flex-col px-4 pb-2.5 pt-1">
                  {projects.length === 0 && <EmptyLine text="Keine To-dos mit verknüpfter Anfrage." />}
                  {projects.map((p) => (
                    <div key={p.lead.id} className="flex flex-col gap-1.5 border-b border-app-border py-2.5 last:border-b-0">
                      <div className="flex min-w-0 items-baseline gap-2">
                        {canOpenProjects ? (
                          <Link
                            to={`${instancePrefix}/projects/${p.lead.id}`}
                            className="truncate text-sm font-medium text-pipedrive-blue hover:underline"
                          >
                            {leadLabel(p.lead)}
                          </Link>
                        ) : (
                          <span className="truncate text-sm font-medium text-app-text">{leadLabel(p.lead)}</span>
                        )}
                        {p.lead.display_id && (
                          <span className="whitespace-nowrap font-mono text-[11px] text-app-text-subtle">{p.lead.display_id}</span>
                        )}
                        <span className="ml-auto whitespace-nowrap text-xs text-app-text-subtle">
                          {p.done} von {p.total} erledigt
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-app-bg">
                          <span className="h-full bg-green-500" style={{ width: `${Math.round((p.done / p.total) * 100)}%` }} />
                        </span>
                        {p.overdue > 0 && (
                          <span className="whitespace-nowrap text-[11px] font-medium text-red-600">{p.overdue} überfällig</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <section className={`${PANEL_CLASS} min-w-0`}>
              <div className="flex flex-wrap items-center gap-3 border-b border-app-border px-4 py-3">
                <span className="text-sm font-semibold text-app-text">Alle To-dos</span>
                <span className="text-xs text-app-text-subtle max-md:text-sm">{countLabel(rows.length)}</span>
                <div className="flex-1" />
                {hasFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      setStatus('all');
                      setWho('all');
                    }}
                    className="px-1 text-xs text-pipedrive-blue hover:underline max-md:text-sm"
                  >
                    Filter zurücksetzen
                  </button>
                )}
                {/* Mobil ohne Suche/Mitarbeiter-Auswahl — Mitarbeiter filtert man dort über die Auslastung oben */}
                <div className="hidden items-center gap-3 md:flex">
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => onQuery(e.target.value)}
                    placeholder="To-dos durchsuchen"
                    aria-label="To-dos durchsuchen"
                    className="h-[30px] w-44 rounded-md border border-app-border bg-app-surface px-2.5 text-xs text-app-text outline-none placeholder:text-app-text-subtle focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                  />
                  <AppSelectDropdown
                    value={who}
                    onChange={setWho}
                    options={whoOptions}
                    className="w-44"
                    triggerClassName={TASK_COMPACT_TRIGGER_CLASS}
                  />
                </div>
                {/* Mobil wischbar statt umbrechend */}
                <div className="-mx-4 w-[calc(100%+32px)] overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:w-auto md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
                  <div className="w-max max-md:[&_button]:text-sm">
                    <SegmentedControl<TeamStatusFilter> options={STATUS_OPTIONS} value={status} onChange={setStatus} ariaLabel="Status filtern" />
                  </div>
                </div>
              </div>
              {/* Mobil: Karten statt der breiten Tabelle */}
              <div className="flex flex-col md:hidden">
                {rows.length === 0 && (
                  <div className="px-4 py-10 text-center text-sm text-app-text-subtle">Keine To-dos für diese Filter.</div>
                )}
                {rows.map((t) => {
                  // Wie in der Liste: Fälligkeit trägt den Status (rot = überfällig), keine zusätzliche Pill.
                  const due = t.done ? { text: doneLabel(t, today), tone: 'default' as const } : dueLabel(t, today);
                  const member = t.assigned_user_id ? membersById.get(t.assigned_user_id) ?? null : null;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onSelect(t)}
                      className={`flex items-center gap-3 border-b border-app-border px-4 py-3 text-left last:border-b-0 ${
                        t.id === selectedId ? 'bg-inbox-selected' : 'active:bg-app-hover'
                      }`}
                    >
                      <TaskAvatar member={member} />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span
                          className={`line-clamp-2 break-words text-sm leading-snug ${t.done ? 'text-app-text-subtle line-through' : 'text-app-text'}`}
                        >
                          {t.title}
                        </span>
                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-app-text-subtle">
                          {due.text && (
                            <span className={`shrink-0 whitespace-nowrap font-medium ${TONE_TEXT_CLASS[due.tone]}`}>{due.text}</span>
                          )}
                          {due.text && <span aria-hidden>·</span>}
                          <span className="truncate">{member ? memberName(member) : 'Nicht zugewiesen'}</span>
                        </span>
                        {t.lead && (
                          <span className="flex min-w-0 items-center gap-1 text-xs text-app-text-subtle">
                            <Link2 className="h-3 w-3 shrink-0" />
                            <span className="truncate">{leadLabel(t.lead)}</span>
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="app-scrollbar hidden overflow-x-auto md:block">
                <div className="min-w-[880px]" role="table" aria-label="Alle To-dos">
                  <div role="row" className={`${TABLE_GRID} h-9 items-center border-b border-app-border bg-app-surface-muted`}>
                    {COLUMNS.map((c) => {
                      const active = sort.key === c.key;
                      const Icon = !active ? ChevronsUpDown : sort.dir > 0 ? ArrowUp : ArrowDown;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          role="columnheader"
                          aria-sort={active ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}
                          onClick={() =>
                            setSort((s) => ({ key: c.key, dir: s.key === c.key ? ((-s.dir) as 1 | -1) : 1 }))
                          }
                          className={`group/sort inline-flex items-center gap-1 text-left text-xs font-semibold ${
                            active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
                          }`}
                        >
                          {c.label}
                          <Icon
                            className={`h-3 w-3 shrink-0 ${active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'}`}
                            aria-hidden
                          />
                        </button>
                      );
                    })}
                  </div>
                  {rows.length === 0 && (
                    <div className="px-4 py-10 text-center text-sm text-app-text-subtle">Keine To-dos für diese Filter.</div>
                  )}
                  {rows.map((t) => {
                    const due = dueLabel(t, today);
                    const member = t.assigned_user_id ? membersById.get(t.assigned_user_id) ?? null : null;
                    return (
                      <div
                        key={t.id}
                        role="row"
                        tabIndex={0}
                        onClick={() => onSelect(t)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onSelect(t);
                        }}
                        className={`${TABLE_GRID} min-h-11 cursor-pointer items-center border-b border-app-border text-sm outline-none transition-colors last:border-b-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pipedrive-blue/30 ${
                          t.id === selectedId ? 'bg-inbox-selected' : 'hover:bg-app-hover'
                        }`}
                      >
                        <span role="cell" className="flex min-w-0 flex-col py-1.5">
                          <span className={`truncate ${t.done ? 'text-app-text-subtle line-through' : 'text-app-text'}`}>
                            {t.title}
                          </span>
                          <span className="font-mono text-[11px] text-app-text-subtle">{t.display_id}</span>
                        </span>
                        <span role="cell" className={`truncate ${t.lead ? 'text-pipedrive-blue' : 'text-app-text-subtle'}`}>
                          {t.lead ? leadLabel(t.lead) : '—'}
                        </span>
                        <span role="cell" className="flex min-w-0 items-center gap-2">
                          <TaskAvatar member={member} size="sm" />
                          <span className="truncate text-app-text-muted">{member ? memberName(member) : 'Nicht zugewiesen'}</span>
                        </span>
                        <span role="cell" className={`whitespace-nowrap text-xs font-medium ${TONE_TEXT_CLASS[due.tone]}`}>
                          {due.text || '—'}
                        </span>
                        <span role="cell" className="justify-self-start">
                          <PriorityPill priority={t.priority} />
                        </span>
                        <span role="cell" className="justify-self-start">
                          <StatusPill status={taskStatus(t, today)} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </section>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`h-2 w-2 rounded-sm ${className}`} />
      {label}
    </span>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <div className="px-2 py-6 text-center text-sm text-app-text-subtle">{text}</div>;
}
